import { SponsorStudySiteIdentity } from './../../sponsor/sites/sponsor-study-site-identity';
import { FilterFieldDef, FilterFieldType, FilterNumberRangeValue } from '../../covance-filter';
import { LabTestResult, Flag } from '../../lab-test-results';
import { uniq, some, isNil, isEmpty, chain } from 'lodash';
import { TestFilterItem } from './filtered-lab-test-results-storage';
import { Injectable } from '@angular/core';
import { CovanceFilterNumberRangeType } from '../../covance-filter/filter-type/covance-filter-number-range-type';
import { SubjectTitleRenderer } from '../../renderer/subject-title-renderer';

export const trendsSiteFilterTitle = 'Site';

@Injectable()
export class OnScreenResultsFilterDefenitions {

    public readonly siteDataFilterFieldDefs: FilterFieldDef[];
    
    public readonly trendsFilterFieldDefs: FilterFieldDef[];

    private readonly visitFilter: FilterFieldDef = {
        title: 'Visit',
        queryParam: 'visitName',
        isMatch: (selectedValues: string[], entity: LabTestResult) => {
            return !isNil(entity.visitName)
                ? some(selectedValues, (val: string) => {
                    if (isNil(val)) {
                        return false;
                    }

                    return entity.visitName.toLowerCase() === val.toLowerCase();
                })
                : false;
        },
        type: FilterFieldType.MultiSelect
    };

    private readonly visitCollectionDateFilter: FilterFieldDef = {
        title: 'Collected Date',
        queryParam: 'visitCollectionDate',
        type: FilterFieldType.DateRange
    };

    private readonly accessionNumberFilter: FilterFieldDef = {
        title: 'Accession',
        queryParam: 'accessionNumber',
        type: FilterFieldType.MultiSelect,
        order: 100,
        compare: (a: number, b: number) => {
            return a - b;
        },
        isMatch: (selectedValues: string[], entity: LabTestResult) => {
            return some(selectedValues, val => val === entity.accessionNumber);
        }
    };

    private readonly testGroupFilter: FilterFieldDef = {
        title: 'Test Group',
        queryParam: 'testGroup',
        type: FilterFieldType.MultiSelect
    };

    private readonly testFilter: FilterFieldDef = {
        title: 'Test',
        queryParam: 'test',
        type: FilterFieldType.MultiSelect,
        getItems: (entities: LabTestResult[]) => this.getUniqueTestsWithUnits(entities),
        isMatch: (selectedKeys: TestFilterItem[], entity: LabTestResult) => this.isMatchToTestsWithUnits(selectedKeys, entity),
    };

    private readonly valueFilter: FilterFieldDef = {
        title: 'Value',
        queryParam: 'value',
        type: FilterFieldType.NumberRange,
        isMatch: (range: FilterNumberRangeValue, entity: LabTestResult) => {
            if (entity.isCancelled || entity.currentResult === null) {
                return false;
            }

            return this.filterNumberRangeType.isMatchByRange(range, entity.currentResult ? entity.currentResult.numericResult : null);
        }
    };

    private readonly isFlaggedFilter: FilterFieldDef = {
        title: 'Flagged Values',
        queryParam: 'isFlagged',
        type: FilterFieldType.Checkbox
    };

    private readonly isQuestionFilter: FilterFieldDef = {
        title: 'Administrative Questions',
        queryParam: 'isQuestion',
        type: FilterFieldType.Checkbox
    };

    private readonly flagsFilter: FilterFieldDef = {
        title: 'Flags',
        queryParam: 'flags',
        type: FilterFieldType.MultiSelect,
        getItems: (entities: LabTestResult[]) => this.getUniqueFlags(entities),
        isMatch: (selectedFlagKeys: string[], entity: LabTestResult) => this.isMatchToFlags(selectedFlagKeys, entity.flags)
    };

    private readonly subjectFilter: FilterFieldDef = {
        title: 'Subject',
        queryParam: 'initials',
        type: FilterFieldType.MultiSelect,
        order: 200,
        getItems: (entities: LabTestResult[]) => {
            return uniq(entities.map(entity => SubjectTitleRenderer.renderTitle({ data: entity })));
        },
        isMatch: (selectedValues: string[], entity: LabTestResult) => {
            const subjectTitle = SubjectTitleRenderer.renderTitle({ data: entity }).toLowerCase();
            return some(selectedValues, val => subjectTitle.indexOf(val.toLowerCase()) !== -1);
        }
    };

    private readonly genderFilter: FilterFieldDef = {
        title: 'Gender',
        queryParam: 'sex',
        type: FilterFieldType.MultiSelect
    };

    private readonly siteFilter: FilterFieldDef = {
        title: trendsSiteFilterTitle,
        queryParam: 'siteNumber',
        type: FilterFieldType.MultiSelect,
        isMatch: (selectedSites: {key: string, value: SponsorStudySiteIdentity}[], entity: LabTestResult) => {
            return !!(selectedSites.find(filteItem => filteItem.value.siteNumber === entity.siteNumber));
        }
    };

    private readonly ageFilter: FilterFieldDef = {
        title: 'Age',
        queryParam: 'age',
        type: FilterFieldType.NumberRange,
    };

    private readonly ageProjectFilter: FilterFieldDef = {
        title: 'Age',
        queryParam: 'daysLived',
        type: FilterFieldType.AgeRange,
    };

    private readonly flaggedTrendsFilter: FilterFieldDef = {
        title: 'Flagged Values',
        queryParam: 'isFlagged',
        type: FilterFieldType.Checkbox,
        isMatch: (isFlagged: boolean, entity: LabTestResult) => isFlagged === entity.trendFlags.length > 0
    };

    private readonly flagTrendsFilter: FilterFieldDef = {
        title: 'Flags',
        queryParam: 'flags',
        type: FilterFieldType.MultiSelect,
        getItems: (entities: LabTestResult[]) => this.getUniqueFlags(entities),
        isMatch: (selectedFlagKeys: string[], entity: LabTestResult) => this.isMatchToFlags(selectedFlagKeys, entity.trendFlags)
    };

    constructor(
        private readonly filterNumberRangeType: CovanceFilterNumberRangeType
    ) { 
        this.siteDataFilterFieldDefs = [
            this.subjectFilter,
            this.genderFilter,
            this.ageProjectFilter,
            this.visitFilter,
            this.visitCollectionDateFilter,
            this.accessionNumberFilter,
            this.testGroupFilter,
            this.testFilter,
            this.valueFilter,
            this.isFlaggedFilter,
            this.isQuestionFilter,
            this.flagsFilter
        ];
        
        this.trendsFilterFieldDefs = [
            this.subjectFilter,
            this.visitFilter,
            this.genderFilter,
            this.siteFilter,
            this.ageProjectFilter,
            this.flaggedTrendsFilter,
            this.flagTrendsFilter,
        ];
    }

    private getUniqueFlags(entities: LabTestResult[]): string[] {
        if (isEmpty(entities)) {
            return [];
        }

        return chain(entities)
            .map((e: LabTestResult) => e.flags)
            .flatten()
            .uniqBy((f: Flag) => f.fullName)
            .map((f: Flag) => f.fullName)
            .value();
    }

    private isMatchToFlags(selectedFlagKeys: string[], entityFlags: Flag[]) {
        return entityFlags.some((f: Flag) => selectedFlagKeys.some(k => k === f.fullName));
    }

    private getUniqueTestsWithUnits(entities: LabTestResult[]): TestFilterItem[] {
        if (isEmpty(entities)) {
            return [];
        }

        return chain(entities)
            .orderBy(e => e.testNameWithUnit || '', 'desc')
            .uniqBy((e: LabTestResult) => e.zcTestCode)
            .map((e: LabTestResult) => new TestFilterItem(e.zcTestCode, e.testNameWithUnit))
            .orderBy((e: TestFilterItem) => e.toString())
            .value();
    }

    private isMatchToTestsWithUnits(selectedKeys: TestFilterItem[], entity: LabTestResult) {
        return selectedKeys.some(t => t.key === entity.zcTestCode);
    }

}
